<template>
  <v-container class="modern-detail-view pb-12">
    <ValidationObserver ref="observer" v-slot="{ handleSubmit, invalid, validated }">
      <!-- Global Header -->
      <v-row class="mb-8 align-center px-4 pt-4">
        <v-btn icon @click.stop="$router.push({name:'PurchaseOrders'})" class="mr-4 grey lighten-4 shadow-sm">
          <v-icon color="primary" small>fas fa-chevron-left</v-icon>
        </v-btn>
        <div class="d-flex align-center">
          <v-avatar color="primary" size="48" class="mr-4 shadow-sm border-white-sm">
            <v-icon color="white">fas fa-shopping-basket</v-icon>
          </v-avatar>
          <div>
            <div class="text-h4 font-weight-black primary--text lh-1">
              {{ id > 0 ? `Actualizar Orden de Compra #${id}` : 'Nueva Orden de Compra' }}
            </div>
            <div class="mt-1 d-flex align-center">
              <v-chip :color="tipo ? 'primary' : 'secondary'" dark x-small label class="font-weight-black px-2 mr-2">
                <v-icon x-small left>{{ tipo ? 'fas fa-boxes' : 'fas fa-tools' }}</v-icon>
                {{ tipo ? 'Artículos' : 'Servicios' }}
              </v-chip>
              <span class="caption grey--text font-weight-bold uppercase tracking-widest">Gestión de abastecimiento</span>
            </div>
          </div>
        </div>
        <v-spacer></v-spacer>
        
        <!-- Botón con estado visual de error si el formulario es inválido y ha sido validado -->
        <v-btn 
          :color="invalid && validated ? 'error' : 'primary'" 
          class="font-weight-black shadow-sm px-8" 
          @click="handleSubmit(confirm)" 
          :loading="loading"
        >
          <v-icon left small>{{ invalid && validated ? 'fas fa-exclamation-triangle' : 'fas fa-save' }}</v-icon> 
          {{ invalid && validated ? 'Corregir Errores' : 'Guardar Orden' }}
        </v-btn>
      </v-row>

      <v-form @submit.prevent="handleSubmit(confirm)" @keydown.enter.prevent>
        <v-row>
          <!-- Configuration Card -->
          <v-col cols="12" md="4">
            <v-card outlined class="modern-card shadow-sm pa-6 mb-8 fill-height" :class="invalid && validated ? 'border-error-light' : ''">
              <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-6">Proveedor y Tipo</div>
              
              <ValidationProvider name="Proveedor" rules="required" v-slot="{ errors }">
                <provider-complete 
                  v-model="provider" 
                  :error_messages="errors" 
                  :provider_name="provider_name" 
                  @added="addProvider"
                  class="mb-4"
                />
                <div v-if="errors[0]" class="caption error--text mb-4 ml-1 mt-n3 font-weight-bold">
                  <v-icon x-small color="error" class="mr-1">fas fa-circle-exclamation</v-icon>
                  {{ errors[0] }}
                </div>
              </ValidationProvider>

              <div class="caption grey--text font-weight-black mb-1 ml-1">Tipo de Orden</div>
              <ValidationProvider name="Tipo" rules="required" v-slot="{ errors }">
                <v-select
                  v-model="tipo"
                  :items="tipos"
                  :loading="loading"
                  :disabled="loading"
                  item-text="text"
                  item-value="value"
                  outlined dense
                  :hide-details="!errors[0]"
                  class="modern-input mb-4"
                  :error-messages="errors"
                  @change="articles=[]"
                >
                  <template v-slot:item="data">
                    <div class="d-flex align-center">
                      <v-icon small class="mr-2" :color="data.item.value ? 'primary' : 'secondary'">
                        {{ data.item.value ? 'fas fa-boxes' : 'fas fa-tools' }}
                      </v-icon>
                      <span class="body-2">{{ data.item.text }}</span>
                    </div>
                  </template>
                  <template v-slot:selection="data">
                    <div class="d-flex align-center">
                      <v-icon x-small class="mr-2" :color="data.item.value ? 'primary' : 'secondary'">
                        {{ data.item.value ? 'fas fa-boxes' : 'fas fa-tools' }}
                      </v-icon>
                      <span class="body-2 font-weight-bold">{{ data.item.text }}</span>
                    </div>
                  </template>
                </v-select>
              </ValidationProvider>

              <div class="caption grey--text font-weight-black mb-1 ml-1">Centro de Costo</div>
              <ValidationProvider name="Centro de Costo" rules="required" v-slot="{ errors }">
                <v-select
                  v-model="center"
                  :items="centers"
                  item-text="name"
                  item-value="id"
                  outlined dense
                  :hide-details="!errors[0]"
                  class="modern-input"
                  :error-messages="errors"
                />
              </ValidationProvider>
            </v-card>
          </v-col>

          <!-- Document Info Card -->
          <v-col cols="12" md="8">
            <v-card outlined class="modern-card shadow-sm pa-6 mb-8">
              <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-6">Información de la Orden</div>
              
              <v-row>
                <v-col cols="12" md="6">
                  <div class="caption grey--text font-weight-black mb-1 ml-1">Condición de Pago</div>
                  <ValidationProvider name="Condición de Pago" rules="required" v-slot="{ errors }">
                    <v-text-field
                      v-model='payment'
                      placeholder="Ej: 30"
                      outlined dense
                      :hide-details="!errors[0]"
                      class="modern-input"
                      :error-messages="errors"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>
                <v-col cols="12" md="6">
                  <div class="caption grey--text font-weight-black mb-1 ml-1">Moneda</div>
                  <ValidationProvider name="Moneda" rules="required" v-slot="{ errors }">
                    <v-text-field
                      v-model='currency'
                      outlined dense
                      :hide-details="!errors[0]"
                      class="modern-input"
                      :error-messages="errors"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>
                <v-col cols="12">
                  <div class="caption grey--text font-weight-black mb-1 ml-1">Referencia / Glosa</div>
                  <ValidationProvider name="Referencia" rules="required" v-slot="{ errors }">
                    <v-text-field
                      v-model='title'
                      placeholder="Identificador rápido de la orden..."
                      outlined dense
                      :hide-details="!errors[0]"
                      class="modern-input"
                      :error-messages="errors"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>
              </v-row>
            </v-card>

            <!-- OT Link Card -->
            <v-card outlined class="modern-card shadow-sm pa-6">
              <div class="d-flex align-center mb-6">
                <div class="caption grey--text font-weight-black text-uppercase tracking-widest">Vinculación a OT</div>
                <v-spacer></v-spacer>
                <v-switch
                  v-model="linkToWorkOrder"
                  hide-details
                  dense
                  class="mt-0 pt-0 modern-switch"
                  @change="()=>{ if(!linkToWorkOrder){ work_order=''; work_order_details=''; work_order_finded=false; } }"
                ></v-switch>
              </div>

              <v-row v-if="linkToWorkOrder">
                <v-col cols="12" md="4">
                  <div class="caption grey--text font-weight-black mb-1 ml-1">Número de OT</div>
                  <v-text-field
                    v-model='work_order'
                    outlined dense hide-details
                    placeholder="Ej: 10245"
                    class="modern-input"
                    :append-icon="work_order_finded ? 'fas fa-check-circle' : 'fas fa-search'"
                    :loading="loadingWorkOrder"
                    @keydown="searchWorkOrder"
                    :success="work_order_finded"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="8">
                  <v-slide-y-transition>
                    <div v-if="work_order_finded" class="pa-4 bg-lightgrey rounded-lg border-dashed fill-height d-flex flex-column justify-center">
                      <div class="caption grey--text font-weight-black mb-1">DETALLES DE LA OT</div>
                      <div class="body-2 font-weight-bold grey--text text--darken-3">{{ work_order_details }}</div>
                    </div>
                  </v-slide-y-transition>
                </v-col>
              </v-row>
              <div v-else class="text-center py-2 grey--text text-caption font-italic">
                 Active el interruptor para vincular esta orden a una Orden de Trabajo específica
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Items Section -->
        <div class="mt-12 mb-4 d-flex align-center">
          <v-avatar color="primary" size="32" class="mr-3 shadow-xs">
            <v-icon color="white" x-small>{{ tipo ? 'fas fa-boxes' : 'fas fa-tools' }}</v-icon>
          </v-avatar>
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest">
            Detalle de {{ tipo ? 'Artículos' : 'Servicios' }}
          </div>
          <v-spacer></v-spacer>
          <div v-if="!tipo" class="d-flex align-center gap-2" style="width: 400px">
            <v-text-field
              v-model='service'
              placeholder="Nombre del servicio..."
              hide-details
              outlined dense
              class="modern-input"
              @keydown.enter.prevent="addService"
            ></v-text-field>
            <v-btn color="primary" icon class="grey lighten-4 shadow-xs" @click="addService">
              <v-icon small>fas fa-plus</v-icon>
            </v-btn>
          </div>
          <div v-else style="min-width: 400px">
            <add-article @add="addArticle" />
          </div>
        </div>

        <v-card outlined class="modern-card shadow-sm overflow-hidden mb-8" :class="articles.length === 0 && validated ? 'border-error' : ''">
          <v-data-table
            :headers="filteredHeaders"
            :items="articles"
            :single-expand="true"
            item-key="id"
            show-expand
            hide-default-footer
            :items-per-page="-1"
            class="modern-table"
          >
            <template v-slot:expanded-item="{ headers, item }">
              <td :colspan="headers.length">
                <v-row align="center" class="py-4 px-6 bg-lightgrey mx-0 rounded-lg">
                  <v-col cols="12" md="4" v-if="tipo">
                    <div class="d-flex flex-column">
                      <span class="caption grey--text font-weight-black uppercase mb-2">Historial de Compra</span>
                      <div class="d-flex align-center mb-1">
                        <v-icon x-small color="grey" class="mr-2">fas fa-tag</v-icon>
                        <span class="body-2">Último Precio: <strong>{{ item.last_buy_price | moneda }}</strong></span>
                      </div>
                      <div class="d-flex align-center mb-1">
                        <v-icon x-small color="grey" class="mr-2">fas fa-cubes</v-icon>
                        <span class="body-2">Última Cantidad: <strong>{{ item.last_buy_qty }}</strong></span>
                      </div>
                      <div class="d-flex align-center">
                        <v-icon x-small color="grey" class="mr-2">fas fa-calendar-alt</v-icon>
                        <span class="body-2">Última Fecha: <strong>{{ item.last_buy_date | moment("DD/MM/YYYY") }}</strong></span>
                      </div>
                    </div>
                  </v-col>
                  <v-col>
                    <div class="caption grey--text font-weight-black uppercase mb-1">Notas / Observaciones del Ítem</div>
                    <v-text-field
                      v-model='item.notes'
                      placeholder="Ingrese notas adicionales para este ítem..."
                      hide-details
                      outlined dense
                      class="modern-input-minimal"
                    ></v-text-field>
                  </v-col>
                </v-row>
              </td>
            </template>
            
            <template v-slot:no-data>
              <div class="pa-10 text-center fill-width">
                <v-icon size="48" color="grey lighten-2" class="mb-4">fas fa-shopping-cart</v-icon>
                <div class="body-1 grey--text font-weight-bold uppercase mb-2">La orden está vacía</div>
                <div class="caption grey--text mb-4">Agregue artículos o servicios para continuar</div>
                <v-alert v-if="validated && articles.length === 0" dense type="error" outlined class="d-inline-flex px-6 caption font-weight-bold">
                  Es obligatorio agregar al menos un ítem
                </v-alert>
              </div>
            </template>

            <template v-slot:item.unit_name="{ item }">
              <div v-if="tipo">{{ item.unit_name }}</div>
            </template>

            <template v-slot:item.stock="{ item }">
              <div v-if="tipo" class="d-flex align-center">
                <span class="body-2 font-weight-bold" :class="item.total_stock <= 0 ? 'error--text' : ''">
                  {{ item.total_stock | number('0,0[.]0') }}
                </span>
                <v-menu bottom left offset-y transition="slide-y-transition" content-class="modern-card" @click.stop>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon x-small v-bind="attrs" v-on="on" class="ml-1">
                      <v-icon x-small color="primary">fas fa-chevron-down</v-icon>
                    </v-btn>
                  </template>
                  <v-sheet class="pa-0 overflow-hidden">
                    <v-sheet color="primary" dark class="px-4 py-2 caption font-weight-black uppercase tracking-wider">
                      Stock por Bodega
                    </v-sheet>
                    <div v-if="item.stocks && item.stocks.length > 0">
                      <v-simple-table dense class="modern-table-compact">
                        <thead>
                          <tr>
                            <th class="text-left py-2 px-4 grey lighten-4 caption font-weight-bold grey--text">Bodega</th>
                            <th class="text-right py-2 px-4 grey lighten-4 caption font-weight-bold grey--text">Existencia</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for='register in item.stocks' :key='register.id'>
                            <td class="px-4 py-2 body-2" :class="register.add_stock ? '' : 'error--text font-italic'">
                              {{ register.store_name }}
                            </td>
                            <td class="px-4 py-2 text-right body-2 font-weight-bold" :class="register.add_stock ? '' : 'error--text'">
                              {{ register.qty | number('0,0[.]0') }}
                            </td>
                          </tr>
                        </tbody>
                      </v-simple-table>
                    </div>
                    <div v-else class="pa-6 text-center">
                      <v-icon color="grey lighten-1" size="32" class="mb-2">fas fa-warehouse</v-icon>
                      <div class="caption grey--text font-weight-bold uppercase">Sin existencias</div>
                    </div>
                  </v-sheet>
                </v-menu>
              </div>
            </template>

            <template v-slot:item.qty="{ item }">
              <v-text-field
                type="number"
                v-model.number='item.qty'
                :min="1"
                hide-details
                outlined dense
                class="modern-input-minimal text-center mx-auto"
                style="max-width: 120px"
              ></v-text-field>
            </template>

            <template v-slot:item.price="{ item }">
              <v-text-field
                type="number"
                v-model.number='item.price'
                :min="0"
                outlined dense hide-details
                class="modern-input-minimal text-right"
                style="max-width: 150px"
              ></v-text-field>
            </template>

            <template v-slot:item.total="{ item }">
              <span class="body-2 font-weight-black primary--text">
                {{ (item.price * item.qty) | moneda }}
              </span>
            </template>

            <template v-slot:item.actions="{ item }">
              <v-btn icon small @click.stop="removeArticle(item)" class="grey lighten-4">
                <v-icon color="error" small>fas fa-trash</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card>

        <!-- Totals & Notes Section -->
        <v-row class="mt-8 px-4">
          <v-col cols="12" md="7">
            <v-card outlined class="modern-card shadow-sm pa-6 fill-height">
              <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-4">Comentarios Adicionales</div>
              <v-textarea
                v-model="notes"
                placeholder="Ingrese términos, condiciones o notas generales para esta orden de compra..."
                outlined
                rows="6"
                hide-details
                class="modern-input"
              ></v-textarea>
            </v-card>
          </v-col>
          <v-col cols="12" md="5">
            <v-card outlined class="modern-card shadow-sm pa-6 bg-lightgrey fill-height">
              <div class="d-flex justify-space-between align-center mb-3">
                <span class="caption grey--text font-weight-black uppercase">Sub Total Neto</span>
                <span class="body-1 font-weight-bold">{{ neto | moneda }}</span>
              </div>
              
              <div class="d-flex justify-space-between align-center mb-4">
                <div class="d-flex align-center">
                  <span class="caption grey--text font-weight-black uppercase mr-2">Descuento (%)</span>
                  <v-text-field 
                    outlined dense hide-details
                    class='modern-input-minimal'
                    style="width: 70px"
                    type='number' 
                    v-model.number="discount"
                    :min="0"
                  />
                </div>
                <span class="body-2 font-weight-bold error--text">- {{ (neto * discount / 100) | moneda }}</span>
              </div>

              <div class="d-flex justify-space-between align-center mb-4">
                <div class="d-flex align-center">
                  <span class="caption grey--text font-weight-black uppercase mr-2">{{ $store.getters.getInternational.purchase_tax_label }} (%)</span>
                  <v-text-field 
                    outlined dense hide-details
                    class='modern-input-minimal'
                    style="width: 70px"
                    type='number' 
                    v-model.number="iva"
                    :min="0"
                  />
                </div>
                <span class="body-2 font-weight-bold">{{ ((neto - neto * discount / 100) * iva / 100) | moneda }}</span>
              </div>

              <v-divider class="my-4 border-dashed"></v-divider>
              
              <div class="d-flex justify-space-between align-center">
                <span class="body-1 font-weight-black primary--text uppercase tracking-wider">Total Orden</span>
                <span class="text-h4 font-weight-black primary--text">{{ ((neto - neto * discount / 100) * (1 + iva / 100)) | moneda }}</span>
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-form>
    </ValidationObserver>

    <v-dialog v-model="showProgress" persistent max-width="500">
      <v-card class="pa-10"> 
        <v-card-title class="titulo text-h4">Registrando...</v-card-title>
        <v-row>
          <v-col>
            <v-progress-linear v-model="progress" />
          </v-col>
        </v-row>
        <v-row justify="center">
          <v-col class="text-center">
            <h1 class="text text-h4 titulo">{{progress | number('0.0')}}%</h1>
          </v-col>
        </v-row>
      </v-card>
    </v-dialog>

    <confirm 
      v-model="showConfirm" 
      :message="id>0?'Se actualizará la Orden de Compra #'+id:'Se creará una nueva Orden de Compra'" 
      @ok="create"
      @cancel="showConfirm=false"
    />
  </v-container>
</template>

<script>
import {fetchCenters, createPurchase, getPurchase, getArticle, updatePurchase} from '@/api/stocks'
import {getWorkOrder} from '@/api/production'
import ProviderComplete from '@/components/stocks/ProviderComplete'
import AddArticle from '@/components/stocks/AddArticle'
import notifications from '@/utils/notifications'
import moment from 'moment'

export default {
  name:'CreatePurchaseOrder',
  components:{AddArticle, ProviderComplete},
  data() {
    return {
      loading: false,
      showConfirm:false,
      discount:0,
      provider:'',
      provider_name:'',
      payment:'',
      iva:parseInt(this.$store.getters.getCompany.tax),
      currency:'',
      centers:[],
      center:'',
      notes:'',
      title:'',
      service:'',
      articles:[],
      progress:'',
      showProgress:false,
      id:-1,
      tipo:true,
      linkToWorkOrder:false,
      work_order:'',
      work_order_details:'',
      loadingWorkOrder:false,
      work_order_finded:false,
      timer:'',
      tipos:[
        { value:true, text:'Artículos' },
        { value:false, text:'Servicios' },
      ],
      headers:[
        { text: 'Código', align: 'start', sortable: false, value: 'sku', show:false, class:'table-header' },
        { text: 'Nombre', align: 'start', sortable: false, value: 'name', show:true, class:'table-header' },
        { text: 'Unidad', align: 'start', sortable: false, value: 'unit_name', show:false, class:'table-header' },
        { text: 'Existencia', align: 'start', sortable: false, value: 'stock', show:false, class:'table-header' },
        { text: 'Cantidad', align: 'start', sortable: false, value: 'qty', width:'10%', show:true, class:'table-header' },
        { text: 'Precio Unitario', align: 'start', sortable: false, value: 'price', show:true, width:'12%', class:'table-header' },
        { text: 'Total', align: 'end', sortable: false, value: 'total', show:true, class:'table-header' },
        { text: '', align: 'end', sortable: false, value: 'actions', show:true, class:'table-header' },
      ]
    }
  },
  methods:{
    storeStock(item){
      const stock=item.stocks.filter(s=>s.store==this.store)
      if(stock.length==0) return {qty:0,id:-1}
      else return {qty: stock[0].qty,id:stock[0].id}
    },
    totalStock(item){
      return item.stocks.filter(i=>i.add_stock).reduce((acc, val)=>{ return acc + parseFloat(val.qty) }, 0)
    },
    addArticle(item){
      const storeStock=this.storeStock(item)
      item.store_stock=storeStock.qty
      item.store_stock_id=storeStock.id
      item.total_stock=this.totalStock(item)
      item.qty=1
      item.price=0
      const alreadyExist=this.articles.filter(a=>a.id==item.id).length
      if(alreadyExist<1){
        this.articles.push(item)
      }
    },
    addService(){
      if(this.service){
        this.articles.push({
          id:Math.random().toString(36).substr(2, 9),
          sku:'',
          name:this.service,
          qty:1,
          price:0,
          notes:'',
          stocks:[],
          store_stock:[],
        })
      }
      this.service=''
    },
    removeArticle(item){
      this.articles=this.articles.filter(i=>i.id!=item.id)
    },
    confirm(){
      if(this.articles.length<=0){
        // El observer debería manejar esto visualmente, pero mantenemos la notificación por seguridad
        notifications.showError('Deben existir items en la Orden')
        return 0
      }
      this.showConfirm=true
    },
    searchWorkOrder() {  
      if (this.timer) {
          clearTimeout(this.timer);
      }
      this.timer = setTimeout(async () => {
          if(!this.work_order) return
          this.loadingWorkOrder = true
          await getWorkOrder(this.work_order)
            .then((data)=>{
              this.work_order_finded=true 
              this.loadingWorkOrder=false
              const {customer_name,created_at,name}=data
              this.work_order_details=`${customer_name} ${moment(created_at).format('DD/MM/yyy')} ${name}`
            })
            .catch((e)=>{
              notifications.showInfo('Orden de Trabajo no encontrada')
              this.work_order_finded=false
              this.loadingWorkOrder=false
            })
      }, 800);
    },
    async create(){
      if(this.articles.length<=0){
        notifications.showError('Deben existir items en la Orden')
        return 0
      }
      this.loading=true
      this.showConfirm=false
      const {stocks_po_approve_limit}=this.$store.getters.getCompany
      let details=''
      if(this.tipo){
        details = this.articles.map(item=>{
          return {
            article: item.id,
            qty: parseFloat(item.qty).toFixed(2),
            price: parseFloat(item.price).toFixed(4),
            notes:item.notes
          }
        })
      }
      else{
        details = this.articles.map(item=>{
          return {
            name: item.name,
            qty: parseFloat(item.qty).toFixed(2),
            price: parseFloat(item.price).toFixed(4),
            notes:item.notes
          }
        })
      }
      
      let data={
        provider:this.provider.id,
        payment:this.payment,
        title:this.title,
        notes:this.notes,
        moneda:this.currency,
        discount:parseFloat(this.discount).toFixed(2),
        stock_service:this.tipo,
        center:this.center,
        details:details,
        iva:this.iva,
        estatus:this.neto*(1-this.discount/100)>stocks_po_approve_limit?1:2
      }
      if(this.work_order_finded) data={...data,work_order:this.work_order}
      else data={...data,work_order:null}
      
      if(this.id<0){
        await createPurchase(data)
        .then((res)=>{
          notifications.showSuccess('Orden de Compra '+res.id+' creada')
          this.$router.go(-1)
        })
        .catch(err=>{
          notifications.showError('Error creando Orden de compra')
        })
      }
      else{
        await updatePurchase(this.id,data,'force=true')
        .then((res)=>{
          notifications.showSuccess('Orden de Compra '+res.id+' actualizada')
          this.$router.go(-1)
        })
        .catch(err=>{
          notifications.showError('Error actualizando Orden de compra')
        })
      }
      this.loading=false
    },
    addProvider(item){
      if(!item) {
        this.provider = ''
        this.provider_name = ''
        this.payment = ''
        return
      }
      this.provider=item
      this.provider_name=item.name
      this.payment = (item.payment == 0) ? 'CONTADO' : `${item.payment} DÍAS`
    }
  },
  computed:{
    neto(){
      return this.articles.reduce((acc,val)=>{return acc + parseFloat(val.qty)*parseFloat(val.price);},0) 
    },
    filteredHeaders(){
      return this.headers.filter(h=>h.show||this.tipo)
    },
    validated(){
      return this.$refs.observer ? this.$refs.observer.flags.validated : false
    }
  },
  async mounted(){
    const {default_currency}= this.$store.getters.getCompany
    this.currency=default_currency
    if (this.$route.params.id>0){
      this.id=this.$route.params.id
      await getPurchase(this.id)
      .then((ocData)=>{
        this.provider={id:ocData.provider}
        this.provider_name=ocData.provider_name
        this.payment = (ocData.payment == 0) ? 'CONTADO' : `${ocData.payment} DÍAS`
        this.currency=ocData.moneda
        this.center=ocData.center
        this.notes=ocData.notes
        this.title=ocData.title
        this.tipo=ocData.stock_service
        this.iva=parseInt(ocData.iva)
        this.discount=parseFloat(ocData.discount || 0)
        
        if (this.tipo){
          ocData.details.forEach(async item=>{
            await getArticle(item.article)
              .then(data=>{
                data.total_stock=this.totalStock(data)
                data.qty=item.qty
                data.price=parseFloat(item.price)
                data.notes=item.notes
                this.articles.push(data)
              })
              .catch(err=>{ console.error(err) })
          })
        }
        else {
          ocData.details.forEach(item=>{
            this.articles.push({
              ...item,
              id: Math.random().toString(36).substr(2, 9),
              qty: parseFloat(item.qty),
              price: parseFloat(item.price)
            })
          })
        }
        if(ocData.work_order){
          this.work_order=ocData.work_order
          this.linkToWorkOrder=true
          this.work_order_finded=true
          this.searchWorkOrder()
        }
      })
      .catch(err=>{
        notifications.showError('Error cargando Orden de Compra')
      })
    }
    if(this.$route.params.duplicate){
      this.title='(Copia) '+ (this.title || '')
      this.id=-1
    }
    this.centers = await fetchCenters()
  }
}
</script>

<style scoped>
.modern-detail-view { background-color: #f4f7f9; min-height: 100vh; }
.modern-card { border-radius: 12px !important; border: 1px solid rgba(0,0,0,0.05) !important; background-color: white !important; transition: all 0.3s ease; }
.shadow-sm { box-shadow: 0 4px 12px rgba(0,0,0,0.04) !important; }
.shadow-xs { box-shadow: 0 1px 3px rgba(0,0,0,0.05) !important; }
.border-white-sm { border: 2px solid white !important; }
.border-dashed { border: 1px dashed #e0e4e8 !important; }
.border-error { border: 1px solid var(--v-error-base) !important; }
.border-error-light { border: 1px solid rgba(var(--v-error-base), 0.3) !important; }
.bg-lightgrey { background-color: #f8fbfc; }
.lh-1 { line-height: 1 !important; }
.gap-2 { gap: 8px; }
.uppercase { text-transform: uppercase; }
.tracking-widest { letter-spacing: 1.8px; }
.tracking-wider { letter-spacing: 1.2px; }

.modern-input >>> .v-input__control fieldset { border-radius: 8px !important; border-color: #e0e4e8 !important; }
.modern-input >>> .v-input__control { background-color: white !important; }
.modern-input >>> input { font-size: 0.9rem !important; font-weight: 500 !important; color: #1a1f2e !important; }
.modern-input.v-input--has-state >>> .v-input__control fieldset { border-width: 2px; }

.modern-switch >>> .v-input--selection-controls__ripple { color: var(--v-primary-base) !important; }

.modern-input-minimal >>> .v-input__control fieldset { border-radius: 6px !important; border-color: #f0f3f5 !important; }
.modern-input-minimal >>> input { font-size: 0.85rem !important; padding: 0 8px !important; }

.modern-table >>> thead th { background-color: #f8fbfc !important; height: 48px !important; text-transform: uppercase; font-size: 0.7rem !important; letter-spacing: 1px; font-weight: 900 !important; color: #607d8b !important; }
.modern-table >>> td { height: 64px !important; border-bottom: 1px solid #f0f3f5 !important; }

.modern-table-compact >>> thead th { height: 32px !important; font-size: 0.65rem !important; }
.modern-table-compact >>> td { height: 32px !important; border-bottom: 1px solid #f0f3f5 !important; }

.v-btn.shadow-sm { box-shadow: 0 4px 10px rgba(var(--v-primary-base), 0.2) !important; }
.v-btn.error.shadow-sm { box-shadow: 0 4px 10px rgba(var(--v-error-base), 0.3) !important; }
</style>
